// src/components/ListCard.js
import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ListCard (classic polaroid, inline footer)
 * - Outer: rounded “polaroid”, black outline
 * - Image: 1:1, rounded, never squished
 * - Bottom: Title (top-left), then a single row with creator (left)
 *   and optional badgeRight (right) e.g. "Similarity: 93%" — same
 *   font/color as creator and on the same baseline.
 * - Width uses --card-w so your grids stay perfect
 */

const resolveTitle = (props) => {
  const t =
    props.listName ??
    props.title ??
    props.name ??
    props.displayName ??
    props.label ??
    props.id ??
    'Untitled';
  return String(t);
};

const resolveSubtitle = (props) => {
  // subtitle/creator fallback chain
  const s = props.subtitle ?? props.createdBy ?? props.username ?? '';
  return s ? String(s) : '';
};

const ImageBox = ({ imageUrl }) => {
  const hasImg = !!imageUrl;
  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '1 / 1',
        borderRadius: 16,
        background: hasImg ? `url(${imageUrl}) center/cover no-repeat` : '#eef1f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#9aa3b2',
        fontSize: 14,
        border: '1px solid #e6e9ef',
      }}
    >
      {!hasImg && <span>No image</span>}
    </div>
  );
};

const Body = (props) => {
  const title = resolveTitle(props);
  const subtitle = resolveSubtitle(props);
  const badgeRight = props.badgeRight; // e.g., "Similarity: 93%"
  const footerLeftOverride = props.footerLeftOverride; // <-- NEW: optional, used by My Feed only

  const footerText = {
    fontSize: 12,
    color: '#8a94a6',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div style={{ marginTop: 0, marginLeft: 6 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: 16,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={title}
      >
        {title}
      </div>

      {((subtitle && !footerLeftOverride) || footerLeftOverride || badgeRight) && (
        <div
          style={{
            marginTop: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          {footerLeftOverride ? (
            // My Feed supplies a custom left footer (e.g., "Ranked by: <username>")
            <div style={{ paddingRight: 6 }}>
              {footerLeftOverride}
            </div>
          ) : (
            // Default behavior everywhere else
            <div style={{ ...footerText, paddingRight: 6 }} title={subtitle}>
              {subtitle}
            </div>
          )}
          {badgeRight ? (
            <div style={footerText} title={badgeRight}>
              {badgeRight}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const CardShell = (props) => {
  const content = (
    <div
      // NOTE: no global .card/.card-body classes—prevents App.css overrides
      style={{
        width: 'var(--card-w)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 8,
        borderRadius: 16,              // classic polaroid roundness
        background: '#fff',
        border: '2px solid #0e0e0e',   // black outline
      }}
    >
      <ImageBox imageUrl={props.imageUrl} />
      <Body {...props} />
    </div>
  );

  return props.to ? (
    <Link to={props.to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {content}
    </Link>
  ) : (
    content
  );
};

export default function ListCard(props) {
  return <CardShell {...props} />;
}
